import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import React, { FC, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
`
const Head = styled.div<{ isInApp: boolean }>`
  font-size: 3.3rem;
  color: var(--text-color);
  max-width: 668px;
  width: 100%;
  margin: 0 2rem 4rem;
  ${(props) => (props.isInApp ? `margin-top: 2rem;` : '')}
  text-size-adjust: 100%;
`
const TitleHeader = styled.span<{ tenantKey?: string; isFree?: boolean }>`
  display: inline-block;
  font-size: 1.5rem;
  font-family: var(--font-sans-serif);
  text-size-adjust: 100%;
  font-weight: 600;
  /* white-space: nowrap; */
  color: ${(props) =>
    props.tenantKey
      ? 'var(--tenant-' + props.tenantKey + ')'
      : 'var(--tenant-tagesanzeiger)'};
  line-height: 2rem;
  margin-bottom: 0.3rem;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  &::before {
    background-color: ${(props) =>
      props.tenantKey
        ? 'var(--tenant-' + props.tenantKey + ')'
        : 'var(--tenant-tagesanzeiger)'};
    display: ${(props) => (props.isFree ? 'none' : 'inline-block')};
    margin-right: 0.6rem;
    padding: 0.2rem 0.6rem;
    color: white;
    font-weight: 700;
    font-size: 1.2rem;
    font-family: var(--font-sans-serif);
    line-height: 1.5rem;
    letter-spacing: 0.025rem;
    white-space: nowrap;
    content: 'Abo';
  }
  /* margin-right: 4ch; */
`
const HiddenDash = styled.span`
  display: none;
`
const TitleText = styled.span`
  font-family: var(--font-source);
  @media (min-width: 541px) {
    font-size: 4.9rem;
    line-height: 5.2rem;
  }
  font-weight: 600;
  font-size: 3.3rem;
  line-height: 3.8rem;
`
const Title = styled.h2<{ tenantKey?: string }>`
  text-size-adjust: 100%;
  margin-top: 0.3rem;
  margin-bottom: 1.2rem;
  font-size: 10px;
`
const Lead = styled.h3`
  font-family: var(--font-sans-serif);
  font-weight: 400;
  line-height: 2.9rem;
  font-size: 2rem;
  margin-bottom: 4rem;
`
const Authors = styled.div`
  color: var(--text-color);
  font-size: 1.2rem;
  font-family: var(--font-sans-serif);
  line-height: 1.8rem;
  border-top: 1px solid var(--border-color);
  padding-top: 2rem;
  a {
    color: var(--text-color);
    text-decoration: none;
    border-bottom: 1px solid var(--text-color);
  }
  span::after {
    content: ', ';
  }
  span:last-child::after {
    content: '';
  }
`

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties,
}): ReactElement => {
  const defaultProperties = {
    // Default values used if they don't come from Unity
    bfsIdParamName: 'gemeinde',
    fallbackTitle: 'So heiss wird es in 40 Jahren',
    fallbackLead:
      'Dieser Sommer ist nur ein Vorgeschmack darauf, was uns in Zukunft öfter und intensiver erwartet. Mit welchem Klima wir in der Stadt bald rechnen müssen.',
    authors:
      'Yannick Wiget[28754714], Patrick Vögeli[494487854856], Mathias Lutz[95724327]',
    isFree: false,
    fallbackHeading: 'Blick in die Klimazukunft',
  }

  const propertiesWithDefaults = { ...defaultProperties, ...properties }
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]

  const [dynamicTitle, setDynamicTitle] = useState<string>()
  const [dynamicLead, setDynamicLead] = useState<string>()
  const [dynamicHeading, setDynamicHeading] = useState<string>()
  const [isInApp, setIsInApp] = useState(false)

  useEffect(() => {
    // GET PARAMS FROM URL
    const searchParams = new URL(String(window.location)).searchParams
    const muniParam = searchParams.get(propertiesWithDefaults.bfsIdParamName!)
    const bfsIdFromUrl = muniParam && parseInt(muniParam)

    // Check if it is in webview to give more margin-top
    setIsInApp(new URL(window.location.href).searchParams.has('appwebview'))
  }, [])

  let authors = (propertiesWithDefaults.authors || '').split(', ')

  let tenantCity = 'Zürich'
  if (tenant) {
    // console.log('tenant:', tenant)
    switch (tenant.key) {
      case 'bazonline':
        tenantCity = 'Basel'
        break

      case 'derbund':
      case 'bernerzeitung':
      case 'berneroberlaender':
      case 'thunertagblatt':
      case 'langenthalertagblatt':
        tenantCity = 'Bern'
        break
      default:
        tenantCity = 'Zürich'
    }
  }

  return (
    <Wrapper className={globalClasses.join(' ')}>
      <Head isInApp={isInApp}>
        <Title tenantKey={tenant?.key}>
          <TitleHeader
            isFree={propertiesWithDefaults?.isFree}
            tenantKey={tenant?.key}
          >
            {dynamicHeading ||
              propertiesWithDefaults?.fallbackHeading ||
              'Please pass a fallbackHeading to the component'}
          </TitleHeader>
          <HiddenDash aria-hidden='true' role='presentation'>
            {' '}
            –{' '}
          </HiddenDash>
          <TitleText>
            {'So heiss wird es in ' + tenantCity + ' in 40 Jahren' ||
              propertiesWithDefaults?.fallbackTitle ||
              'Please pass a fallbackTitle to the component'}
          </TitleText>
        </Title>
        <Lead>
          {dynamicLead ||
            propertiesWithDefaults?.fallbackLead ||
            'Please pass a fallbackLead to the component'}
        </Lead>
        {propertiesWithDefaults?.authors && (
          <Authors>
            {authors.map((m, i) => {
              let parts = m.split('[')
              let name = parts[0]
              let unityId = parts[1] && parts[1].replace(']', '')
              if (!unityId) return <span key={name}>{name}</span>
              return (
                <span key={unityId}>
                  <a
                    href={`https://www.${tenant?.key}.ch/author/${unityId}/${name}`}
                  >
                    {name}
                  </a>
                </span>
              )
            })}
          </Authors>
        )}
      </Head>
    </Wrapper>
  )
}
export default MainComponent
